import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from './navigation/Logo';
import DesktopNav from './navigation/DesktopNav';
import MobileNav from './navigation/MobileNav';
import { ServiceLink } from './navigation/types';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

const getServiceLinks = (language: 'en' | 'de'): ServiceLink[] => {
  if (language === 'en') {
    return [
      {
        title: "AI Training & Integration",
        path: "/services/ai-training",
        description: "Custom AI solutions and model development"
      },
      {
        title: "Process Automation",
        path: "/services/process-automation",
        description: "Streamline workflows and reduce manual tasks"
      },
      {
        title: "Digital Solutions",
        path: "/services/digital-solutions",
        description: "Web and mobile application development"
      },
      {
        title: "Chatbot Development",
        path: "/services/chatbots",
        description: "Intelligent conversational agents"
      },
      {
        title: "Data Analytics",
        path: "/services/analytics",
        description: "Transform data into actionable insights"
      },
      {
        title: "Innovation Consulting",
        path: "/services/consulting",
        description: "Strategic guidance for digital transformation"
      }
    ];
  } else {
    return [
      {
        title: "KI-Training & Integration",
        path: "/services/ai-training",
        description: "Maßgeschneiderte KI-Lösungen und Modellentwicklung"
      },
      {
        title: "Prozessautomatisierung",
        path: "/services/process-automation",
        description: "Optimierung von Arbeitsabläufen und Reduzierung manueller Aufgaben"
      },
      {
        title: "Digitale Lösungen",
        path: "/services/digital-solutions",
        description: "Web- und Mobile-Anwendungsentwicklung"
      },
      {
        title: "Chatbot-Entwicklung",
        path: "/services/chatbots",
        description: "Intelligente Konversationsagenten"
      },
      {
        title: "Datenanalyse",
        path: "/services/analytics",
        description: "Transformation von Daten in umsetzbare Erkenntnisse"
      },
      {
        title: "Innovationsberatung",
        path: "/services/consulting",
        description: "Strategische Begleitung für die digitale Transformation"
      }
    ];
  }
};

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [opacity, setOpacity] = useState(0);
  const [rotationY, setRotationY] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const { isDarkMode } = useTheme();
  const { language, setLanguage } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      const heroHeight = window.innerHeight;
      const scrollPosition = window.scrollY;
      const scrollPercentage = Math.min(scrollPosition / (heroHeight * 0.5), 1);
      
      if (window.innerWidth <= 768) {
        setOpacity(isHomePage ? scrollPercentage : 1);
        setRotationY(0);
      } else {
        setOpacity(isHomePage ? scrollPercentage : 1);
        setRotationY(isHomePage ? (scrollPosition / heroHeight) * 180 : 0);
      }
      
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsMobileServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const handleNavigation = (path: string) => {
    setIsMenuOpen(false);
    setIsMobileServicesOpen(false);
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId: string) => {
    setIsMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const section = document.getElementById(sectionId);
        if (section) {
          section.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'de' : 'en');
  };

  const serviceLinks = getServiceLinks(language);

  return (
    <header 
      className={`fixed w-full z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-secondary-black/95 shadow-[0_4px_30px_rgba(0,0,0,0.1)] border-b border-primary-mint/20' 
          : 'bg-gradient-to-b from-secondary-black via-secondary-black/95 to-transparent'
      }`}
    >
      <div className="container mx-auto px-4 relative">
        <div className="flex items-center justify-between h-20 relative z-10">
          <Logo opacity={opacity} rotationY={rotationY} isHomePage={isHomePage} />

          <DesktopNav
            serviceLinks={serviceLinks}
            language={language}
            onLanguageToggle={toggleLanguage}
            onScrollToSection={scrollToSection}
            onNavigate={handleNavigation}
          />

          <button 
            className="md:hidden text-secondary-cream p-2 rounded-full 
                     hover:bg-primary-mint/10 transition-colors active:scale-95 transform relative z-50"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMenuOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
          </button>
        </div>

        <MobileNav
          isOpen={isMenuOpen}
          serviceLinks={serviceLinks}
          isServicesOpen={isMobileServicesOpen}
          language={language}
          onServicesToggle={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
          onLanguageToggle={toggleLanguage}
          onScrollToSection={scrollToSection}
          onNavigate={handleNavigation}
        />
      </div>
    </header>
  );
};

export default Header;